import type { ReactNode } from 'react';
import { FaWordpress } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';
import type { DemoBrand } from '../types';

/**
 * WordPress admin with Settings > Easy-Peasy.AI Chatbot (plugin 1.1): the
 * agent ID pasted in, the plugin naming the agent it found, and the plugin
 * active. The bubble's look comes from the agent, so there is nothing else
 * to fill in.
 */
export default function WordPressPlugin({ brand }: { brand: DemoBrand }) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[32rem] pb-2 sm:pb-12'>
      <div className='flex overflow-hidden rounded-[18px] text-start shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)] ring-1 ring-black/5 dark:ring-white/10'>
        <div className='flex w-12 flex-none flex-col items-center gap-3.5 bg-[#1d2327] py-4'>
          <FaWordpress className='h-6 w-6 text-[#9ea3a8]' />
          {[0, 1, 2, 3, 4].map((item) => (
            <span
              key={item}
              className='h-2.5 w-5 rounded-sm bg-[#3c434a]'
              aria-hidden='true'
            />
          ))}
          <span className='h-8 w-full border-s-4 border-[#72aee6] bg-[#2271b1]' />
        </div>
        <div className='min-w-0 flex-1 bg-[#f0f0f1] p-5 text-[#1d2327] dark:bg-[#1f2226] dark:text-[#e4e6eb]'>
          <p className='text-[17px] font-semibold'>Easy-Peasy.AI Chatbot</p>
          <div className='mt-3 flex items-center gap-2.5 border-s-4 border-[#00a32a] bg-white px-3 py-2.5 text-[13px] dark:bg-[#2c3338]'>
            <span
              className={clsxm(
                'flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white',
                brand.bg,
              )}
              aria-hidden='true'
            >
              {brand.initial}
            </span>
            <span className='min-w-0'>
              {t('Connected to {{name}}.', { name: brand.name })}
            </span>
          </div>
          <div className='mt-4 text-[13px]'>
            <Row label={t('Agent ID')}>
              <span dir='ltr' className='font-mono text-[12px]'>
                8f3c1a7e-…-41d2
              </span>
            </Row>
          </div>
          <span className='mt-5 inline-flex rounded-[3px] bg-[#2271b1] px-3 py-1.5 text-[13px] font-medium text-white'>
            {t('Save Settings')}
          </span>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-center gap-3 rounded-2xl p-3.5 pe-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-3 sm:end-[-1.5rem] sm:mt-0 sm:w-[17.5rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#f3f3f7] dark:bg-white'>
          <FaWordpress className='h-5 w-5 text-[#21759B]' />
        </span>
        <span className='min-w-0 flex-1'>
          <span
            className={clsxm(
              'block truncate text-[13px] font-semibold',
              tone.ink,
            )}
          >
            Easy-Peasy.AI Chatbot
          </span>
          <span className={clsxm('block text-xs', tone.quiet)}>
            {t('Plugin activated')}
          </span>
        </span>
        <span className='flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
          <CheckGlyph className='h-3.5 w-3.5' />
        </span>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='grid items-center gap-1.5 sm:grid-cols-[8rem_1fr] sm:gap-3'>
      <span className='font-semibold'>{label}</span>
      <span className='rounded-[4px] bg-white px-2.5 py-1.5 ring-1 ring-inset ring-[#8c8f94] dark:bg-[#2c3338] dark:ring-[#50575e]'>
        {children}
      </span>
    </div>
  );
}
